import { useCallback, useState, useRef } from 'react';

/**
 * Manages the state of opening/closing multiple dialogs.
 * @param dialogNames - An array of dialog names. Use an enum to define the dialog names.
 * @returns An object with functions to open and close dialogs, plus the name of the current dialog.
 * @example
 * enum DialogNames {
 *    create = 'create',
 *    update = 'update',
 *    archive = 'archive',
 *    }
 *
 * const { openDialog, closeDialog, currentDialog } = useDialogManager(Object.values(DialogNames));
 */
export function useDialogManager<T extends string>(dialogNames: T[]) {
  const cachedDialogNames = useRef<T[]>(dialogNames);
  const [currentDialog, setCurrentDialog] = useState<T | null>(null);

  const openDialog = useCallback(
    (dialogName: T) => {
      if (cachedDialogNames.current.includes(dialogName)) {
        setCurrentDialog(dialogName);
      }
    },
    [cachedDialogNames]
  );

  const closeDialog = useCallback(
    () => setCurrentDialog(null),
    [setCurrentDialog]
  );

  const isDialogOpen = useCallback(
    (dialogName: T) => currentDialog === dialogName,
    [currentDialog]
  );

  return { openDialog, closeDialog, currentDialog, isDialogOpen };
}
